import React from 'react';

import { EditColumnsModal } from '../../../../components/modals/edit-columns-modal';

import {
  RequestNumberColumn,
  OrderNumberColumn,
  SourceColumn,
  CreateDateColumn,
  CustomerNameColumn,
  CustomerEmailColumn,
  AmountColumn,
  TrackingNumberColumn,
  CarrierServiceColumn,
  StatusColumn,
  baseColumnsDefaultConfigs,
  STORE_COLUMN_ID,
} from '../grids/columns';
import { useEnhancedReturnsContext } from '../grids/enhanced-returns-context';
import { STATUS_AWAITING_SHIPMENT, STATUS_PROCESSING, STATUS_FLAGGED, STATUS_COMPLETED } from '../../../../store/returns/returns.helpers';

type EditEnhancedReturnsColumnsModalProps = {
  onClose: () => void;
};

type ColumnDefinition = { id: string; [key: string]: unknown };

const CONFIGURABLE_COLUMNS_BY_STATUS_VIEW: Record<string, ColumnDefinition[]> = {
  [STATUS_AWAITING_SHIPMENT]: [
    RequestNumberColumn,
    OrderNumberColumn,
    SourceColumn,
    CreateDateColumn,
    CustomerNameColumn,
    CustomerEmailColumn,
    AmountColumn,
  ],
  [STATUS_PROCESSING]: [
    RequestNumberColumn,
    OrderNumberColumn,
    SourceColumn,
    CreateDateColumn,
    CustomerNameColumn,
    CustomerEmailColumn,
    TrackingNumberColumn,
    CarrierServiceColumn,
    AmountColumn,
  ],
  [STATUS_FLAGGED]: [
    RequestNumberColumn,
    OrderNumberColumn,
    SourceColumn,
    CreateDateColumn,
    CustomerNameColumn,
    CustomerEmailColumn,
    TrackingNumberColumn,
    CarrierServiceColumn,
    AmountColumn,
  ],
  [STATUS_COMPLETED]: [
    RequestNumberColumn,
    OrderNumberColumn,
    SourceColumn,
    CreateDateColumn,
    CustomerNameColumn,
    CustomerEmailColumn,
    TrackingNumberColumn,
    CarrierServiceColumn,
    AmountColumn,
    StatusColumn,
  ],
};

// Fallback when statusViewId is unrecognized: all configurable columns
const ALL_CONFIGURABLE_COLUMNS: ColumnDefinition[] = [
  RequestNumberColumn,
  OrderNumberColumn,
  SourceColumn,
  CreateDateColumn,
  CustomerNameColumn,
  CustomerEmailColumn,
  TrackingNumberColumn,
  CarrierServiceColumn,
  AmountColumn,
  StatusColumn,
].filter((col) => col.id !== STORE_COLUMN_ID);

export const EditEnhancedReturnsColumnsModal = ({ onClose }: EditEnhancedReturnsColumnsModalProps) => {
  const { columnsConfig, updateColumnsConfig, statusViewId } = useEnhancedReturnsContext();

  const tabColumns = CONFIGURABLE_COLUMNS_BY_STATUS_VIEW[statusViewId] ?? ALL_CONFIGURABLE_COLUMNS;
  const tabColumnIds = new Set(tabColumns.map((col) => col.id));
  const tabDefaultConfigs = baseColumnsDefaultConfigs.filter((config: { id: string }) => tabColumnIds.has(config.id));

  const handleSaveColumnConfiguration = (columns: Array<{ id: string; [key: string]: unknown }>) => {
    // EditColumnsModal represents visibility via presence/absence in the array (no hidden flag).
    // Only keep the tab's columns that the user chose to show; non-tab columns are preserved
    // from the current config so other tabs are unaffected.
    const updatedTabColumns = columns.filter((c) => tabColumnIds.has(c.id));

    if (updatedTabColumns.length === 0) return;

    const preservedNonTabColumns = columnsConfig.filter((c: { id: string }) => !tabColumnIds.has(c.id));
    updateColumnsConfig([...updatedTabColumns, ...preservedNonTabColumns]);
    onClose();
  };

  // Only pass the tab-relevant slice of columnsConfig so the modal's selected count
  // reflects the columns actually shown, not the full cross-tab config.
  const tabColumnConfiguration = columnsConfig.filter((c: { id: string }) => tabColumnIds.has(c.id));

  return (
    <EditColumnsModal
      baseColumns={tabColumns}
      columnConfiguration={tabColumnConfiguration}
      defaultColumnConfigurations={tabDefaultConfigs}
      onClose={onClose}
      saveColumnConfiguration={handleSaveColumnConfiguration}
    />
  );
};
